
import React from 'react';
import { useParams } from 'react-router-dom';
import { useService } from '@/hooks/useService';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import Layout from '@/components/Layout';
import ServiceDetails from '@/components/ServiceDetails';
import { useReviews } from '@/hooks/useReviews';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { ErrorDisplay } from '@/components/ui/error-display';

const ServiceView = () => {
  const { slug } = useParams<{ slug: string }>();
  const { currentCommunity } = useCommunity();
  const serviceQuery = useService(slug);
  const { user } = useAuth();
  const reviewsQuery = useReviews(serviceQuery.data?.id);
  const { t } = useTranslation();
  
  // Show loading state while dependencies or data is being fetched
  const isLoadingDependencies = !currentCommunity?.id;
  const isLoadingService = serviceQuery.isLoading || serviceQuery.isFetching;
  
  if (isLoadingDependencies || isLoadingService) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Only show error or not found after loading is complete
  if (serviceQuery.error) {
    return (
      <Layout>
        <div className="container mx-auto">
          <ErrorDisplay
            type="loading-error"
            showBack={true}
            onRetry={() => serviceQuery.refetch()}
          />
        </div>
      </Layout>
    );
  }

  const service = serviceQuery.data;
  
  // Only show "not found" if we've finished loading dependencies and service, and there's no data
  if (!isLoadingDependencies && !isLoadingService && !service) {
    return (
      <Layout>
        <div className="container mx-auto">
          <ErrorDisplay
            type="not-found"
            showBack={true}
            showRetry={false}
          />
        </div>
      </Layout>
    );
  }

  // If we reach here but service is still undefined, show loading
  if (!service) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const businessId = service.id;
  const businessLogo = service.companyLogo || null;
  
  const isBusinessOwner = user && service.user_id === user.id;
  
  // Filter out only business showcase images (not banner and logo)
  const workImages = [
    service.business_image_1_url,
    service.business_image_2_url,
    service.business_image_3_url
  ].filter(Boolean);
  
  // Define these wrapper functions to handle the mutations
  const createReviewHandler = async (businessId: string, rating: number, comment: string): Promise<void> => {
    if (!reviewsQuery.createReview?.mutate) {
      throw new Error("Create review function is not available");
    }
    
    return new Promise((resolve, reject) => {
      reviewsQuery.createReview.mutate({
        businessId, 
        rating, 
        description: comment
      }, {
        onSuccess: () => resolve(),
        onError: (error: any) => reject(error)
      });
    });
  };
  
  const updateReviewHandler = async (reviewId: string, rating: number, comment: string): Promise<void> => {
    if (!reviewsQuery.updateReview?.mutate) {
      throw new Error("Update review function is not available");
    }
    
    return new Promise((resolve, reject) => {
      reviewsQuery.updateReview.mutate({
        id: reviewId, 
        rating, 
        description: comment
      }, {
        onSuccess: () => resolve(),
        onError: (error: any) => reject(error)
      });
    });
  };
  
  const deleteReviewHandler = async (reviewId: string): Promise<void> => {
    if (!reviewsQuery.deleteReview?.mutate) {
      throw new Error("Delete review function is not available");
    }
    
    return new Promise((resolve, reject) => {
      reviewsQuery.deleteReview.mutate(reviewId, {
        onSuccess: () => resolve(),
        onError: (error: any) => reject(error)
      });
    });
  };

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4">
        <ServiceDetails
          title={service.name}
          description={service.description || ''}
          images={service.images}
          rating={service.rating || 0}
          reviewCount={service.reviewCount || 0}
          price={service.price || t('service.details.contact_pricing')}
          location={service.location || ''}
          contactEmail={service.contactEmail}
          contactPhone={service.contactPhone}
          website={service.website}
          address={service.address}
          businessId={businessId}
          socialLinks={service.socialLinks}
          imageUrl={service.imageUrl}
          companyLogo={businessLogo}
          googleMapsUrl={service.googleMapsUrl}
          reviews={reviewsQuery.reviews || []}
          reviewsLoading={reviewsQuery.isLoading}
          reviewsError={reviewsQuery.error}
          isBusinessOwner={isBusinessOwner}
          userReview={reviewsQuery.userReview || null}
          createReview={createReviewHandler}
          updateReview={updateReviewHandler}
          deleteReview={deleteReviewHandler}
          discount={service.discount}
          isAuthenticated={!!user}
          workImages={workImages}
          openingHours={service.opening_hours}
          ownerName={service.ownerName}
          ownerImageUrl={service.ownerImageUrl}
          isVerified={service.isVerified}
        />
      </div>
    </Layout>
  );
};

export default ServiceView;
